/* ==========================================================================
   VAN LYNN — predictive-search.js
   Shopify Predictive Search API with Section Rendering. Supports keyboard
   navigation (↑/↓/Enter), request cancellation and caching.
   ========================================================================== */
(function () {
  'use strict';
  if (customElements.get('predictive-search')) return;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.defaultBlock = this.querySelector('[data-predictive-default]');
      this.enabled = this.dataset.enabled === 'true';
      this.cache = new Map();
      if (!this.input || !this.enabled) return;
      this.input.addEventListener('input', window.VL.debounce(() => this.onInput(), 260));
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    }

    get term() {
      return this.input.value.trim();
    }

    onInput() {
      const term = this.term;
      if (!term.length) {
        this.results.innerHTML = '';
        this.defaultBlock.hidden = false;
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      this.search(term);
    }

    async search(term) {
      this.defaultBlock.hidden = true;
      if (this.cache.has(term)) return this.render(this.cache.get(term));
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.results.innerHTML = `<p class="predictive__status">${this.dataset.loadingText || ''}</p>`;
      const url = `${window.theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article,query&resources[limit]=8&resources[options][unavailable_products]=last&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const text = await res.text();
        const markup = window.VL.parseHTML(text).querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        this.cache.set(term, markup);
        this.render(markup);
      } catch (err) {
        if (err.name === 'AbortError') return;
        this.results.innerHTML = `<p class="predictive__status">${window.theme.strings.searchError}</p>`;
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', 'true');
      this.selectedIndex = -1;
    }

    onKeydown(e) {
      if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      const options = Array.from(this.results.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      if (e.key === 'Enter') {
        const selected = options[this.selectedIndex];
        if (selected) {
          e.preventDefault();
          selected.querySelector('a')?.click();
        }
        return;
      }
      e.preventDefault();
      options.forEach((o) => o.querySelector('a')?.removeAttribute('aria-selected'));
      this.selectedIndex = e.key === 'ArrowDown'
        ? (this.selectedIndex + 1) % options.length
        : (this.selectedIndex - 1 + options.length) % options.length;
      const current = options[this.selectedIndex];
      current.querySelector('a')?.setAttribute('aria-selected', 'true');
      this.input.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
